import {Checkbox, ListItemText, TextField} from "@mui/material";
import * as React from "react";
import {useState} from "react";
import MenuItem from "@mui/material/MenuItem";
import {DatePicker} from "@mui/x-date-pickers";
import {useTranslation} from "react-i18next";
import {useRecoilValue, useSetRecoilState} from "recoil";
import * as Yup from "yup";
import AdvancedFilter from "../../../component/AdvancedFilter";
import ColouredDot from "../../../component/ColouredDot";
import {addFilter, getFilterValue, renderMultiSelect} from "../../../utils/FilterUtils";
import {asEndOfDay, asStartOfDay} from "../../../utils/FinanceDateUtils";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {
    COLOR_IMPORTED,
    COLOR_LOADING,
    COLOR_PROCESSED,
    eTollReportsDefaultValues,
    ID_FILTER_REPORT_DEALERS,
    ID_FILTER_REPORT_FILE_NAME,
    ID_FILTER_REPORT_IMPORT_DATE_FROM,
    ID_FILTER_REPORT_IMPORT_DATE_TO,
    ID_FILTER_REPORT_LEGAL_ENTITIES,
    ID_FILTER_REPORT_SEQUENCE_NUMBER,
    ID_FILTER_REPORT_STATUSES,
    ID_FILTER_REPORT_TOLLS_NUMBER,
    ID_FILTER_REPORT_VERSION,
    ID_FILTER_SEARCH,
    ID_IMPORTED,
    ID_LOADING,
    ID_PROCESSED,
    STATE_ETOLL_REPORT_ACTIVE_FILTERS
} from "../constants/ETollConstants";

function ETollReportFilter() {
    const {t} = useTranslation('finance', {keyPrefix: 'etoll'});
    const {t: tg} = useTranslation("translation");

    // Use Context
    const {
        globalData: {user},
    } = React.useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));

    const setFilters = useSetRecoilState(STATE_ETOLL_REPORT_ACTIVE_FILTERS);
    const filterDefs = useRecoilValue(STATE_ETOLL_REPORT_ACTIVE_FILTERS);
    const [dealers, setDealers] = useState<any[]>(dealerOptions);
    const [search, setSearch] = useState("");

    const schema = Yup.object().shape({
        fileName: Yup.string(),
        importDateFrom: Yup.date().nullable().test("testFrom", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.importDateTo || (value <= context.parent.importDateTo);
        }).typeError(t('filters.errors.invalid-date')),
        importDateTo: Yup.date().nullable().test("testTo", t('filters.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.importDateFrom || (value >= context.parent.importDateFrom);
        }).typeError(t('filters.errors.invalid-date')),
        sequenceNumber: Yup.string(),
        version: Yup.string(),
        tollsNumber: Yup.string(),
        selectedStatuses: Yup.array(),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne')),
    });

    const defaultValues = {
        fileName: getFilterValue(ID_FILTER_REPORT_FILE_NAME, filterDefs) || "",
        importDateFrom: getFilterValue(ID_FILTER_REPORT_IMPORT_DATE_FROM, filterDefs) || null,
        importDateTo: getFilterValue(ID_FILTER_REPORT_IMPORT_DATE_TO, filterDefs) || null,
        sequenceNumber: getFilterValue(ID_FILTER_REPORT_SEQUENCE_NUMBER, filterDefs) || "",
        version: getFilterValue(ID_FILTER_REPORT_VERSION, filterDefs) || "",
        tollsNumber: getFilterValue(ID_FILTER_REPORT_TOLLS_NUMBER, filterDefs) || "",
        selectedStatuses: getFilterValue(ID_FILTER_REPORT_STATUSES, filterDefs) || [],
        legalEntities:
            getFilterValue(ID_FILTER_REPORT_LEGAL_ENTITIES, filterDefs) || legalEntityOptions.map((x: any) => x.value),
        dealers: getFilterValue(ID_FILTER_REPORT_DEALERS, filterDefs) || [],
    };


    const renderFileName = (field: any) => (
        <TextField
            margin="dense"
            id={"fileName"}
            value={field.value}
            onChange={field.onChange}
            label={t('filters.reports.' + ID_FILTER_REPORT_FILE_NAME)}
            fullWidth
            variant="standard"
        />
    );

    const renderImportDateFrom = (field: any, error: any, state: any, values: any) => (
        <DatePicker label={t('filters.reports.' + ID_FILTER_REPORT_IMPORT_DATE_FROM)}
                    views={['day']}
                    maxDate={values['importDateTo'] !== null ? values['importDateTo'] : null}
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth
                                                        id={"importDateFrom"}
                                                        error={error !== undefined}
                                                        helperText={error ? error.message : ""}
                    />}
                    inputFormat={"L"}
        />
    )
    const renderImportDateTo = (field: any, error: any, state: any, values: any) => (
        <DatePicker label={t('filters.reports.' + ID_FILTER_REPORT_IMPORT_DATE_FROM)}
                    views={['day']}
                    minDate={values['importDateFrom'] !== null ? values['importDateFrom'] : null}
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => <TextField {...params} fullWidth
                                                        id={"importDateTo"}
                                                        error={error !== undefined}
                                                        helperText={error ? error.message : ""}
                    />}
                    inputFormat={"L"}
        />
    )

    const renderSequenceNumber = (field: any) => (
        <TextField
            margin="dense"
            id={"sequenceNumber"}
            value={field.value}
            onChange={field.onChange}
            label={t('filters.reports.' + ID_FILTER_REPORT_SEQUENCE_NUMBER)}
            fullWidth
            variant="standard"
        />
    );

    const renderVersion = (field: any) => (
        <TextField
            margin="dense"
            id={"sequenceNumber"}
            value={field.value}
            onChange={field.onChange}
            label={t('filters.reports.' + ID_FILTER_REPORT_VERSION)}
            fullWidth
            variant="standard"
        />
    );
    const renderTollsNumber = (field: any) => (
        <TextField
            margin="dense"
            id={"tollsNumber"}
            value={field.value}
            onChange={field.onChange}
            label={t('filters.reports.' + ID_FILTER_REPORT_TOLLS_NUMBER)}
            fullWidth
            variant="standard"
        />
    );

    const renderStatuses = (field: any) => (
        <TextField fullWidth
                   label={t('filters.reports.' + ID_FILTER_REPORT_STATUSES)}
                   select
                   SelectProps={{
                       multiple: true,
                       renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
                   }}
                   value={field.value}
                   onChange={field.onChange}
        >
            {statuses.map((status) =>
                <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                    <ColouredDot color={status?.color}/>
                    <ListItemText primary={status.label}/>
                </MenuItem>
            )}
        </TextField>
    )


    const fields = {
        fileName: renderFileName,
        importDateFrom: renderImportDateFrom,
        importDateTo: renderImportDateTo,
        sequenceNumber: renderSequenceNumber,
        version: renderVersion,
        tollsNumber: renderTollsNumber,
        selectedStatuses: renderStatuses,
        legalEntities: renderMultiSelect(
            tg("shared.legalEntities"),
            legalEntityOptions,
            "legalEntities",
            legalEntityOptions.length <= 1,
            (value: any, setValue: any) => {
                setValue('dealers', []);
                let dealers = user?.dealers
                    ?.filter(
                        (x: any) =>
                            value?.length > 0 &&
                            x.legalEntities.some((e: any) => value.includes(e.name)) &&
                            x.name.toLowerCase() !== "all"
                    )
                    .map((x: any) => {
                        return {
                            label: tg(`dealers.${x.name}`),
                            value: x.name,
                        };
                    });
                setDealers(dealers);
            }
        ),
        dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1)
    };


    const statuses = [
        {
            value: ID_LOADING,
            label: t('statuses.' + ID_LOADING),
            color: COLOR_LOADING + '.transparent'
        },
        {
            value: ID_IMPORTED,
            label: t('statuses.' + ID_IMPORTED),
            color: COLOR_IMPORTED + '.transparent'
        },
        {
            value: ID_PROCESSED,
            label: t('statuses.' + ID_PROCESSED),
            color: COLOR_PROCESSED + '.transparent'
        },
    ];

    const submit = (data: any) => {
        addFilter(setFilters, ID_FILTER_SEARCH, search);
        addFilter(setFilters, ID_FILTER_REPORT_FILE_NAME, data.fileName);
        addFilter(setFilters, ID_FILTER_REPORT_IMPORT_DATE_FROM, asStartOfDay(data.importDateFrom));
        addFilter(setFilters, ID_FILTER_REPORT_IMPORT_DATE_TO, asEndOfDay(data.importDateTo));
        addFilter(setFilters, ID_FILTER_REPORT_SEQUENCE_NUMBER, data.sequenceNumber);
        addFilter(setFilters, ID_FILTER_REPORT_VERSION, data.version);
        addFilter(setFilters, ID_FILTER_REPORT_TOLLS_NUMBER, data.tollsNumber);
        addFilter(setFilters, ID_FILTER_REPORT_STATUSES, data.selectedStatuses);
        addFilter(setFilters, ID_FILTER_REPORT_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilters, ID_FILTER_REPORT_DEALERS, data.dealers);
    }

    const reset = () => {
        setFilters([]);
        setDealers(dealerOptions);
    }

    return (
        <AdvancedFilter schema={schema} fields={fields} defaultValues={defaultValues}
                        searchInput={search} setSearchInput={setSearch}
                        onSubmit={submit}
                        onReset={reset}
                        customDefaultValues={eTollReportsDefaultValues}
        />
    )
}

export default ETollReportFilter;